import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/site";

export const VRM_UNAVAILABLE = "Registration lookup is temporarily unavailable. You can still find your vehicle manually.";
export const normalizeRegistration = value => value.toUpperCase().replace(/ /g, "");
export const validRegistration = value => /^(?:[A-Z]{2}[0-9]{2}[A-Z]{3}|[A-Z][0-9]{1,3}[A-Z]{3}|[A-Z]{3}[0-9]{1,3}[A-Z]|[A-Z]{1,3}[0-9]{1,4}|[0-9]{1,4}[A-Z]{1,3})$/.test(value);

export const useRegistrationLookup = () => {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const flight = useRef(null);
  const attempts = useRef(new Map());

  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API}/tuning-database/registration/status`, { signal: controller.signal, timeout: 10000 })
      .then(response => setStatus(response.data))
      .catch(() => { if (!controller.signal.aborted) setStatus({ enabled: false }); });
    return () => { controller.abort(); flight.current?.abort(); };
  }, []);

  const clear = useCallback(() => {
    flight.current?.abort(); flight.current = null;
    setData(null); setError(""); setBusy(false);
  }, []);

  const lookup = useCallback(async value => {
    if (flight.current) return;
    const registration = normalizeRegistration(value);
    setData(null); setError("");
    if (!validRegistration(registration)) {
      setError("Enter a valid UK registration, for example AB12 CDE."); return;
    }
    const previous = attempts.current.get(registration);
    if (previous) { setData(previous.data || null); setError(previous.error || ""); return; }
    if (!status?.enabled) { setError(VRM_UNAVAILABLE); return; }
    const controller = new AbortController();
    flight.current = controller; setBusy(true);
    // A cancelled/uncertain request is never automatically sent again.
    attempts.current.set(registration, { error: VRM_UNAVAILABLE });
    try {
      const response = await axios.post(`${API}/tuning-database/registration`,
        { registration, request_id: crypto.randomUUID() }, { signal: controller.signal, timeout: 12000 });
      attempts.current.set(registration, { data: response.data });
      if (!controller.signal.aborted) setData(response.data);
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      const message = typeof detail?.message === "string" ? detail.message : VRM_UNAVAILABLE;
      attempts.current.set(registration, { error: message });
      if (!controller.signal.aborted) setError(message);
    } finally {
      if (flight.current === controller) { flight.current = null; setBusy(false); }
    }
  }, [status]);

  return { status, data, error, busy, lookup, clear };
};